import React, { useEffect, useState, useMemo, useRef } from 'react';
import api from '../../services/api';
import styles from './EdgettsManagement.module.css';

const EDGE_TTS_ENDPOINT_PREFIX = '/edge-tts';

function EdgettsManagement() {
  const [speakers, setSpeakers] = useState([]);
  const [speakersLoading, setSpeakersLoading] = useState(false);
  const [speakersError, setSpeakersError] = useState('');
  const [selectedSpeaker, setSelectedSpeaker] = useState(null);
  const [selectedMode, setSelectedMode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [result, setResult] = useState(null);
  const [chapters, setChapters] = useState([]);
  const [chaptersLoading, setChaptersLoading] = useState(false);
  const [chaptersError, setChaptersError] = useState('');
  const [selectedChapterId, setSelectedChapterId] = useState('');
  const [resultProgressExpanded, setResultProgressExpanded] = useState(false);
  const [activeTaskId, setActiveTaskId] = useState('');
  const [activeTask, setActiveTask] = useState(null);
  const [polling, setPolling] = useState(false);
  const pollingRef = useRef(null);

  const backendBaseUrl = useMemo(() => {
    const baseUrl = api.defaults.baseURL || '';
    if (!baseUrl) {
      return '';
    }
    const trimmed = baseUrl.replace(/\/?$/, '');
    return trimmed.replace(/\/api$/, '');
  }, []);

  const computeModeAvailability = (chapter) => {
    if (!chapter) {
      return { en: false, cn: false, podcast: false };
    }
    const hasContent = typeof chapter.content === 'string' && chapter.content.trim().length > 0;
    const hasContentTrans = typeof chapter.content_trans === 'string' && chapter.content_trans.trim().length > 0;
    const hasPodcast = typeof chapter.podcast === 'string' && chapter.podcast.trim().length > 0;

    return {
      en: hasContent,
      cn: hasContentTrans,
      podcast: hasPodcast,
    };
  };

  const resolveDefaultMode = (chapter) => {
    const availability = computeModeAvailability(chapter);
    if (availability.en) {
      return 'en';
    }
    if (availability.cn) {
      return 'cn';
    }
    if (availability.podcast) {
      return 'podcast';
    }
    return '';
  };

  const selectedChapter = useMemo(
    () => chapters.find((chapter) => String(chapter.id) === String(selectedChapterId)),
    [chapters, selectedChapterId],
  );

  useEffect(() => {
    if (!selectedChapter) {
      if (selectedMode) {
        setSelectedMode('');
      }
      return;
    }

    const availability = computeModeAvailability(selectedChapter);
    if (selectedMode && availability[selectedMode]) {
      return;
    }

    const fallback = resolveDefaultMode(selectedChapter);
    if (fallback !== selectedMode) {
      setSelectedMode(fallback);
    }
  }, [selectedChapter, selectedMode]);

  useEffect(() => {
    fetchSpeakers();
    fetchChapters();
  }, []);

  useEffect(() => () => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!activeTaskId) {
      return undefined;
    }

    const pollStatus = async () => {
      try {
        const { data } = await api.get(`${EDGE_TTS_ENDPOINT_PREFIX}/tts/${activeTaskId}`);
        setActiveTask(data);
        if (data.status === 'completed' || data.status === 'failed') {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
          setPolling(false);
          if (data.status === 'completed') {
            setResult(data);
            setActiveTask(null);
          } else {
            setSubmitError(data.error || '生成语音失败，请稍后再试');
          }
          setActiveTaskId('');
        }
      } catch (error) {
        console.error('查询任务进度失败:', error);
      }
    };

    pollStatus();
    if (!pollingRef.current) {
      pollingRef.current = setInterval(pollStatus, 2000);
    }

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [activeTaskId]);

  const fetchSpeakers = async () => {
    setSpeakersLoading(true);
    setSpeakersError('');
    try {
      const { data } = await api.get('/speakers/');
      
      // 按名称排序
      const sorted = data.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setSpeakers(sorted);
      
      if (sorted.length > 0) {
        setSelectedSpeaker(sorted[0]);
      } else {
        setSelectedSpeaker(null);
      }
    } catch (error) {
      console.error('获取说话人列表失败:', error);
      setSpeakersError('获取说话人列表失败，请稍后重试');
    } finally {
      setSpeakersLoading(false);
    }
  };

  const fetchChapters = async () => {
    setChaptersLoading(true);
    setChaptersError('');
    try {
      const { data } = await api.get('/chapters/', { params: { limit: 500 } });
      const list = Array.isArray(data) ? data : [];
      setChapters(list);

      if (list.length === 0) {
        setSelectedChapterId('');
        setSelectedMode('');
        return;
      }

      const existing = list.find((chapter) => String(chapter.id) === String(selectedChapterId));
      const target = existing || list[0];
      setSelectedChapterId(String(target.id));
      setSelectedMode(resolveDefaultMode(target));
    } catch (error) {
      console.error('获取章节列表失败:', error);
      setChaptersError('获取章节列表失败，请稍后重试');
    } finally {
      setChaptersLoading(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitError('');
    setResult(null);
    setActiveTask(null);
    setActiveTaskId('');
    setPolling(false);
    setResultProgressExpanded(false);

    if (!selectedSpeaker) {
      setSubmitError('请选择要使用的说话人');
      return;
    }

    if (!selectedChapterId) {
      setSubmitError('请选择章节');
      return;
    }

    if (!selectedMode) {
      setSubmitError('请选择生成类型');
      return;
    }

    const chapter = selectedChapter;
    if (!chapter) {
      setSubmitError('未找到所选章节，请刷新后重试');
      return;
    }

    const availability = computeModeAvailability(chapter);
    if (!availability[selectedMode]) {
      const modeFieldMap = {
        cn: 'content',
        en: 'content_trans',
        podcast: 'podcast',
      };
      setSubmitError(`所选章节缺少 ${modeFieldMap[selectedMode] || selectedMode} 内容`);
      return;
    }

    try {
      setSubmitting(true);
      const { data } = await api.post(
        `${EDGE_TTS_ENDPOINT_PREFIX}/tts/chapter`,
        {
          chapter_id: Number(chapter.id),
          mode: selectedMode,
          voice: selectedSpeaker.api_name,
        },
      );

      if (data?.task_id) {
        setActiveTaskId(data.task_id);
        setPolling(true);
        setActiveTask({
          status: data.status,
          voice: data.voice,
          chapter_id: data.chapter_id,
          mode: data.mode,
        });
      } else {
        setResult(data);
        setActiveTask(null);
      }
    } catch (error) {
      console.error('生成语音失败:', error);
      const message = error?.response?.data?.detail || '生成语音失败，请稍后再试';
      setSubmitError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const renderSpeakerOptionLabel = (speaker) => {
    const parts = [speaker.name];
    if (speaker.gender) {
      parts.push(`[${speaker.gender}]`);
    }
    if (speaker.age) {
      parts.push(`[${speaker.age}]`);
    }
    if (speaker.language) {
      parts.push(`| ${speaker.language}`);
    }
    if (speaker.is_multi_languages) {
      parts.push('| 多语言');
    }
    if (speaker.tag) {
      parts.push(`| ${speaker.tag}`);
    }
    return parts.join(' ');
  };

  const buildAssetUrl = (relativePath) => {
    if (!relativePath) {
      return '';
    }
    if (!backendBaseUrl) {
      return relativePath;
    }
    const trimmedPath = relativePath.replace(/^\//, '');
    return `${backendBaseUrl}/${trimmedPath}`;
  };

  const selectedChapterAvailability = computeModeAvailability(selectedChapter);
  const chapterContentLengths = {
    en: selectedChapter && typeof selectedChapter.content === 'string' ? selectedChapter.content.length : 0,
    cn: selectedChapter && typeof selectedChapter.content_trans === 'string' ? selectedChapter.content_trans.length : 0,
    podcast: selectedChapter && typeof selectedChapter.podcast === 'string' ? selectedChapter.podcast.length : 0,
  };

  const modeOptions = [
    { value: 'cn', label: '中文（content）' },
    { value: 'en', label: '英文（content_trans）' },
    { value: 'podcast', label: '播客文本（podcast）' },
  ];

  return (
    <div className={styles.edgettsManagement}>
      <div className={styles.header}>
        <button
          type="button"
          className={styles.refreshBtn}
          onClick={() => {
            fetchSpeakers();
            fetchChapters();
          }}
          disabled={speakersLoading || chaptersLoading}
        >
          {(speakersLoading || chaptersLoading) ? '刷新中...' : '刷新数据'}
        </button>
      </div>

      <div className={styles.panel}>
        <form className={styles.form} onSubmit={handleSubmit}>
          <div className={styles.formRow}>
            <label className={styles.label} htmlFor="speakerSelect">
              选择说话人
            </label>
            <select
              id="speakerSelect"
              className={styles.select}
              value={selectedSpeaker?.id || ''}
              onChange={(event) => {
                const speakerId = parseInt(event.target.value);
                const speaker = speakers.find(s => s.id === speakerId);
                setSelectedSpeaker(speaker || null);
              }}
              disabled={speakersLoading || speakers.length === 0}
            >
              {speakers.length === 0 && <option value="">暂无可用说话人</option>}
              {speakers.map((speaker) => (
                <option key={speaker.id} value={speaker.id}>
                  {renderSpeakerOptionLabel(speaker)}
                </option>
              ))}
            </select>
            {speakersError && <div className={styles.error}>{speakersError}</div>}
            {selectedSpeaker && (
              <div className={styles.hint}>
                API名称: {selectedSpeaker.api_name}
              </div>
            )}
          </div>

          <div className={styles.formRow}>
            <label className={styles.label} htmlFor="chapterSelect">
              选择章节
            </label>
            <select
              id="chapterSelect"
              className={styles.select}
              value={selectedChapterId}
              onChange={(event) => {
                setSelectedChapterId(event.target.value);
                setSubmitError('');
                setResult(null);
              }}
              disabled={chaptersLoading || chapters.length === 0}
            >
              {chapters.length === 0 && <option value="">暂无可用章节</option>}
              {chapters.map((chapter) => (
                <option key={chapter.id} value={chapter.id}>
                  {chapter.volume?.novel?.title ? `${chapter.volume.novel.title} / ` : ''}
                  {chapter.volume?.title ? `${chapter.volume.title} / ` : ''}
                  {chapter.title}
                </option>
              ))}
            </select>
            {chaptersError && <div className={styles.error}>{chaptersError}</div>}
            {selectedChapter && (
              <div className={styles.hint}>
                可用内容：
                英文 JSON：{selectedChapterAvailability.en ? `${chapterContentLengths.en} 字` : '无'}；
                中文 JSON：{selectedChapterAvailability.cn ? `${chapterContentLengths.cn} 字` : '无'}；
                播客文本：{selectedChapterAvailability.podcast ? `${chapterContentLengths.podcast} 字` : '无'}
              </div>
            )}
          </div>

          <div className={styles.formRow}>
            <label className={styles.label} htmlFor="modeSelect">
              生成类型
            </label>
            <select
              id="modeSelect"
              className={styles.select}
              value={selectedMode || ''}
              onChange={(event) => {
                setSelectedMode(event.target.value);
                setSubmitError('');
                setResult(null);
              }}
              disabled={!selectedChapter || chaptersLoading}
            >
              <option value="">请选择生成类型</option>
              {modeOptions.map((option) => {
                const disabled = !selectedChapterAvailability[option.value];
                const optionLabel = `${option.label}${disabled ? '（不可用）' : ''}`;
                return (
                  <option key={option.value} value={option.value} disabled={disabled}>
                    {optionLabel}
                  </option>
                );
              })}
            </select>
            {!selectedChapter && (
              <div className={styles.hint}>请选择章节后再选择类型</div>
            )}
            {selectedChapter && selectedMode && selectedChapterAvailability[selectedMode] && (
              <div className={styles.hint}>
                输出文件：static/4_edgetts_srt_mp3/{selectedChapterId}/{selectedMode}.mp3 / {selectedMode}.srt
              </div>
            )}
            {selectedChapter
              && !selectedChapterAvailability.en
              && !selectedChapterAvailability.cn
              && !selectedChapterAvailability.podcast && (
                <div className={styles.error}>该章节暂无可用内容</div>
            )}
          </div>

          {submitError && <div className={styles.error}>{submitError}</div>}

          <div className={styles.actions}>
            <button type="submit" className={styles.submitBtn} disabled={submitting || polling}>
              {submitting || polling ? '生成中...' : '生成 TTS 音频'}
            </button>
          </div>
        </form>
      </div>

      {(result || activeTask) && (
        <div className={styles.resultCard}>
          <h2 className={styles.resultTitle}>生成结果</h2>
          <ul className={styles.resultList}>
            {activeTask && (
              <li>
                <span>任务状态：</span>
                <strong>{activeTask.status}</strong>
              </li>
            )}
            {typeof (result?.sentences_processed ?? activeTask?.sentences_processed) !== 'undefined' && (
              <li>
                <span>处理句子：</span>
                <strong>{result?.sentences_processed ?? activeTask?.sentences_processed}</strong>
              </li>
            )}
            <li>
              <span>使用发音人：</span>
              <strong>{result?.voice ?? activeTask?.voice ?? selectedSpeaker?.api_name}</strong>
            </li>
            {(result?.mode ?? activeTask?.mode) && (
              <li>
                <span>生成类型：</span>
                <strong>{result?.mode ?? activeTask?.mode}</strong>
              </li>
            )}
            {(result?.chapter_id ?? activeTask?.chapter_id) && (
              <li>
                <span>章节 ID：</span>
                <strong>{result?.chapter_id ?? activeTask?.chapter_id}</strong>
              </li>
            )}
            {typeof (result?.total_sentences ?? activeTask?.total_sentences) !== 'undefined' && (
              <li>
                <span>总句子数：</span>
                <strong>{result?.total_sentences ?? activeTask?.total_sentences}</strong>
              </li>
            )}
            {result?.characters && (
              <li>
                <span>字符数：</span>
                <strong>{result.characters}</strong>
              </li>
            )}
            {result?.static_audio_file && (
              <li>
                <span>静态音频：</span>
                <a
                  href={buildAssetUrl(result.static_audio_file)}
                  target="_blank"
                  rel="noreferrer"
                >
                  {result.static_audio_file}
                </a>
              </li>
            )}
            {result?.static_srt_file && (
              <li>
                <span>静态字幕：</span>
                <a
                  href={buildAssetUrl(result.static_srt_file)}
                  target="_blank"
                  rel="noreferrer"
                >
                  {result.static_srt_file}
                </a>
              </li>
            )}
            {result?.audio_file && (
              <li>
                <span>音频文件：</span>
                <a
                  href={buildAssetUrl(result.audio_file)}
                  target="_blank"
                  rel="noreferrer"
                >
                  {result.audio_file}
                </a>
              </li>
            )}
            {result?.srt_file && (
              <li>
                <span>字幕文件：</span>
                <a
                  href={buildAssetUrl(result.srt_file)}
                  target="_blank"
                  rel="noreferrer"
                >
                  {result.srt_file}
                </a>
              </li>
            )}
          </ul>
          {Array.isArray((result?.progress ?? activeTask?.progress)) && (result?.progress ?? activeTask?.progress).length > 0 && (
            <div className={styles.progressSection}>
              <button
                type="button"
                className={styles.progressToggle}
                onClick={() => setResultProgressExpanded(prev => !prev)}
              >
                {resultProgressExpanded ? '隐藏进度详情' : '显示进度详情'}
              </button>
              {resultProgressExpanded && (
                <div className={styles.progressTableWrapper}>
                  <table className={styles.progressTable}>
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>句子 ID</th>
                        <th>文本预览</th>
                        <th>耗时 (ms)</th>
                        <th>开始 (ms)</th>
                        <th>结束 (ms)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(result?.progress ?? activeTask?.progress).map((item, index) => (
                        <tr key={index}>
                          <td>{item.index}</td>
                          <td>{item.sentence_id ?? '-'}</td>
                          <td title={item.text}>{item.text?.slice(0, 40) || ''}{item.text && item.text.length > 40 ? '…' : ''}</td>
                          <td>{item.duration_us ? Math.round(item.duration_us / 1000) : '-'}</td>
                          <td>{typeof item.start_ms === 'number' ? Math.round(item.start_ms) : '-'}</td>
                          <td>{typeof item.end_ms === 'number' ? Math.round(item.end_ms) : '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default EdgettsManagement;
